import { Box } from "@mui/material"
import { alpha, type Theme } from "@mui/material/styles"
import { BulkDeleteDialog } from "@/components/ui/bulk-delete-dialog"

interface Prospect {
  id: string | number
  full_name?: string
  company_name?: string
  email?: string
  priority?: string
}

interface BulkDeleteProspectModalProps {
  isOpen: boolean
  onClose: () => void
  selectedProspects: Prospect[]
  onConfirmDelete?: (prospectIds: string[]) => Promise<void>
}

/** Priority pill tint — mirrors `getPriorityColor` (Tailwind) on the MUI palette. */
function priorityPalette(priority: string | undefined): "error" | "warning" | "info" | "grey" {
  switch (priority) {
    case "urgent": return "error"
    case "high":   return "warning"
    case "medium": return "info"
    case "low":    return "grey"
    default:       return "grey"
  }
}

export function BulkDeleteProspectModal({
  isOpen,
  onClose,
  selectedProspects,
  onConfirmDelete,
}: BulkDeleteProspectModalProps) {
  return (
    <BulkDeleteDialog
      isOpen={isOpen}
      onClose={onClose}
      items={selectedProspects}
      entityLabel={{ singular: "Prospect", plural: "Prospects" }}
      subtitle="They leave the lists; the records are kept"
      // crm-api `leads#bulk_delete` calls `soft_delete!` per row, exactly like
      // the single delete — so this must not claim a cascade that never runs.
      cascadingWarning="This marks all selected prospects as deleted, so they leave the prospect lists, search and reports. Their notes, activities and files are preserved, and the records are kept — an administrator can restore them."
      renderItem={(p) => (
        <>
          {p.full_name || p.company_name || `Prospect ${p.id}`}
          {p.company_name && p.full_name && (
            <Box component="span" sx={{ color: "error.main" }}> ({p.company_name})</Box>
          )}
        </>
      )}
      renderItemBadge={(p) =>
        p.priority ? (
          <Box
            component="span"
            sx={(theme: Theme) => {
              const tone = priorityPalette(p.priority)
              const main = tone === "grey" ? theme.palette.grey[600] : theme.palette[tone].main
              return {
                display: "inline-flex",
                alignItems: "center",
                px: 0.75,
                py: 0.25,
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 500,
                color: main,
                bgcolor: alpha(main, 0.12),
              }
            }}
          >
            {p.priority}
          </Box>
        ) : null
      }
      onConfirm={async (ids) => {
        if (onConfirmDelete) await onConfirmDelete(ids)
      }}
    />
  )
}
